import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { marketService } from '../services/marketService';

const MarketWatchPage = () => {
  const [marketData, setMarketData] = useState([]);
  const [recentIPOs, setRecentIPOs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [indices, ipoPerformance] = await Promise.all([
          marketService.getMarketIndices(),
          marketService.getRecentIPOPerformance()
        ]);
        setMarketData(indices);
        setRecentIPOs(ipoPerformance);
      } catch (error) {
        console.error('Failed to fetch market data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              IPO Tracker
            </div>
            <div className="flex space-x-8">
              <Link to="/" className="text-gray-600 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors">
                Home
              </Link>
              <Link to="/market-watch" className="bg-blue-100 text-blue-700 px-3 py-2 rounded-md text-sm font-medium">
                Market Watch
              </Link>
              <Link to="/ipos" className="text-gray-600 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors">
                IPOs
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Market Watch</h1>
          <p className="text-gray-600">Real-time market data and IPO performance</p>
        </div>
        
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Market Indices</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {loading ? (
              Array(3).fill(0).map((_, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))
            ) : (
              marketData.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">{item.symbol}</h3>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{item.price}</div>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    item.positive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    <span className={`mr-1 ${item.positive ? 'text-green-600' : 'text-red-600'}`}>
                      {item.positive ? '↗' : '↘'}
                    </span>
                    {item.change} ({item.changePercent})
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Recent IPO Performance</h2>
          <div className="space-y-4">
            {loading ? (
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 animate-pulse">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="h-6 bg-gray-200 rounded mb-2 w-48"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                  <div className="text-right">
                    <div className="h-6 bg-gray-200 rounded mb-2 w-20"></div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                </div>
              </div>
            ) : (
              recentIPOs.map((ipo, index) => (
                <Link key={index} to={`/ipo/${index + 1}`} className="block">
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{ipo.name}</h4>
                        <p className="text-gray-600">Listed at {ipo.listingPrice}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">{ipo.currentPrice}</div>
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          ipo.positive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          <span className={`mr-1 ${ipo.positive ? 'text-green-600' : 'text-red-600'}`}>
                            {ipo.positive ? '↗' : '↘'}
                          </span>
                          {ipo.change}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketWatchPage;